import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Form, Input, DatePicker, Select, Card, message, Space, Row, Col, Table, Radio, Spin } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { fetchSqlData, updateData } from '../../../../../services/api';
import WPermitSelect from '../../../../../components/modals/WPermitSelect';
import type { QaqcPipeRfiForms } from '../../../../../types';
import PersonnelSelect from '../../../../../components/modals/PersonnelSelect';
import { taskService } from '../../../../../components/common/TaskManagement/taskService';
import { useGetPersonnel } from '../../../../../hooks/genericHooks';
import { WeldSelectModal } from '../../../../../components/modals';
import type { WeldSelectValue } from '../../../../../components/modals/WeldSelectModal';
import FileUploadTool, { FileUploadToolHandle } from '../../../../../components/common/FileUploadTool';
import FileDownloadTool from '../../../../../components/common/FileDownloadTool';
import Button from '../../../../../components/common/Button';

const { Option } = Select;
const { TextArea } = Input;

type Mode = 'edit' | 'view';

interface PipeRfiFormSharedProps { mode: Mode }

const QaqcPipeRfiFormShared: React.FC<PipeRfiFormSharedProps> = ({ mode }) => {
  const isView = mode === 'view';
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [loadingView, setLoadingView] = useState(false);
  const [formIdLabel, setFormIdLabel] = useState<string | number | undefined>(undefined);
  const [currentStatus, setCurrentStatus] = useState<string | undefined>(undefined);
  const [showAppointInvite, setShowAppointInvite] = useState(false);
  const [appModalOpen, setAppModalOpen] = useState(false);
  const [persModalOpen, setPersModalOpen] = useState(false);
  const [invModalOpen, setInvModalOpen] = useState(false);
  const [recModalOpen, setRecModalOpen] = useState(false);
  const [wpModalOpen, setWpModalOpen] = useState(false);
  const { data: personnelData } = useGetPersonnel();
  const queryClient = useQueryClient();
  const [selectedWelds, setSelectedWelds] = useState<WeldSelectValue[]>([]);
  const [selectedWeldDetails, setSelectedWeldDetails] = useState<any[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [weldModalOpen, setWeldModalOpen] = useState(false);
  const [weldModalMode, setWeldModalMode] = useState<'add' | 'remove'>('add');
  const fileUploadRef = React.useRef<FileUploadToolHandle>(null);
  const [uploadPath, setUploadPath] = useState<string>('');

  const displayWeldRows = React.useMemo(() => {
    const detailById = new Map<string, any>();
    (selectedWeldDetails || []).forEach((d: any) => detailById.set(String(d.weldingdata_id), d));
    return (selectedWelds || []).map((w) => {
      const id = String(w.weldingdata_id);
      const d = detailById.get(id);
      return { weldingdata_id: id, weld_no_rev: w.weld_no_rev, ...(d || {}) };
    }).map((r) => ({ ...r, key: `${String(r.weldingdata_id)}|${String(r.weld_no_rev ?? '')}` }));
  }, [selectedWelds, selectedWeldDetails]);
  useEffect(() => {
    const validKeys = new Set((displayWeldRows || []).map(r => r.key));
    setSelectedRowKeys(prev => prev.filter(k => validKeys.has(String(k))));
  }, [displayWeldRows]);

  useEffect(() => {
    const load = async () => {
      const id = params.id ? Number(params.id) : NaN;
      if (!Number.isFinite(id)) return;
      setLoadingView(true);
      try {
        const res = await fetchSqlData('qaqc_pipe_rfi_forms', 'id', id);
        const row = Array.isArray(res?.data) ? res.data[0] : (Array.isArray(res) ? res[0] : null);
        if (row) {
          let issuerPidResolved: string | undefined;
          try {
            const list = (personnelData?.data || []) as any[];
            if (Array.isArray(list) && row.issued_by) {
              const m = list.find((p: any) => `${p.first_name || ''} ${p.last_name || ''}`.trim() === String(row.issued_by).trim());
              issuerPidResolved = m ? String(m.pers_id || m.id || '') : undefined;
            }
          } catch {}
          form.setFieldsValue({
            issued_by: row.issued_by ?? undefined,
            issued_by_id: issuerPidResolved,
            request_date: row.request_date ? dayjs(row.request_date) : undefined,
            rfi_subject: row.rfi_subject ?? undefined,
            rfi_notes: row.rfi_notes ?? undefined,
            rfi_status: row.rfi_status ?? 'draft',
            recipients: row.recipients ?? [],
            recipient_ids: row.recipient_ids ?? [],
            recipients_display: Array.isArray(row.recipients) ? row.recipients.join('; ') : undefined,
            work_permit_ref_num: row.work_permit_ref_num ?? undefined,
            work_permit_description: row.work_permit_description ?? '',
            inspection_type: row.inspection_type,
            appointees: row.appointees ?? [],
            appointee_ids: row.appointee_ids ?? [],
            invitees: row.invitees ?? [],
            invitee_ids: row.invitee_ids ?? [],
            appointees_display: Array.isArray(row.appointees) ? row.appointees.join('; ') : undefined,
            invitees_display: Array.isArray(row.invitees) ? row.invitees.join('; ') : undefined,
            weld_count: Array.isArray(row.weldingdata_ids) ? row.weldingdata_ids.length : 0,
          });
          setCurrentStatus(row.rfi_status);
          setFormIdLabel(row.form_id);
          setUploadPath((row as any)?.upload_path || '');
          try {
            const ids: string[] = Array.isArray(row.weldingdata_ids) ? row.weldingdata_ids : [];
            const revs: (number | null)[] = Array.isArray(row.weld_no_revs) ? row.weld_no_revs : [];
            const basics: WeldSelectValue[] = ids.map((id: string, idx: number) => ({ weldingdata_id: id, weld_no_rev: (revs[idx] as any) ?? null }));
            setSelectedWelds(basics);
            const pipe_sizes: (string | null)[] = Array.isArray((row as any).pipe_sizes) ? (row as any).pipe_sizes : [];
            const service_class_names: (string | null)[] = Array.isArray((row as any).service_class_names) ? (row as any).service_class_names : [];
            const spool_ids: (number | null)[] = Array.isArray((row as any).spool_ids) ? (row as any).spool_ids : [];
            const weld_categories: (string | null)[] = Array.isArray((row as any).weld_categories) ? (row as any).weld_categories : [];
            const code_nos: (string | null)[] = Array.isArray((row as any).code_nos) ? (row as any).code_nos : [];
            const weldtype_names: (string | null)[] = Array.isArray((row as any).weldtype_names) ? (row as any).weldtype_names : [];
            const fluids: (string | null)[] = Array.isArray((row as any).fluids) ? (row as any).fluids : [];
            const details = ids.map((id: string, idx: number) => ({
              weldingdata_id: id,
              weld_no_rev: (revs[idx] as any) ?? null,
              pipesize_name: pipe_sizes[idx] ?? null,
              serviceclass_name: service_class_names[idx] ?? null,
              spool_id: spool_ids[idx] ?? null,
              weld_category: weld_categories[idx] ?? null,
              code_no: code_nos[idx] ?? null,
              weldtype_name: weldtype_names[idx] ?? null,
              fluid: fluids[idx] ?? null,
            }));
            setSelectedWeldDetails(details);
          } catch {}
        }
      } finally {
        setLoadingView(false);
      }
    };
    load();
  }, [params.id, form, personnelData]);

  const onFinish = async (values: any) => {
    if (isView) return; // no-op in view
    const id = params.id ? Number(params.id) : NaN;
    if (!Number.isFinite(id)) return;

    const getIssuerPid = (): string | undefined => {
      const explicit = form.getFieldValue('issued_by_id');
      if (explicit) return String(explicit);
      const issuedByName: string | undefined = form.getFieldValue('issued_by');
      const list = (personnelData?.data || []) as any[];
      const match = issuedByName
        ? list.find((p: any) => `${p.first_name || ''} ${p.last_name || ''}`.trim() === (issuedByName || '').trim())
        : undefined;
      return match ? String(match.pers_id || match.id || '') : undefined;
    };

    const issuerPid = getIssuerPid();
    const idsSet = new Set<string>(Array.isArray(values.recipient_ids) ? values.recipient_ids : []);
    if (issuerPid) idsSet.add(issuerPid);
    const currentPid = localStorage.getItem('personnel_id') || '';
    if (currentPid && issuerPid !== currentPid) idsSet.delete(currentPid);
    const recipient_ids = Array.from(idsSet);

    const detailById = new Map<string, any>();
    (selectedWeldDetails || []).forEach((d: any) => detailById.set(String(d.weldingdata_id), d));
    const weldingdata_ids = selectedWelds.map(w => w.weldingdata_id);
    const weld_no_revs = selectedWelds.map(w => (w.weld_no_rev === null || w.weld_no_rev === undefined) ? null : Number(w.weld_no_rev));
    const pipe_sizes: (string | null)[] = [];
    const service_class_names: (string | null)[] = [];
    const spool_ids: (number | null)[] = [];
    const weld_categories: (string | null)[] = [];
    const code_nos: (string | null)[] = [];
    const weldtype_names: (string | null)[] = [];
    const fluids: (string | null)[] = [];
    selectedWelds.forEach(w => {
      const id = String(w.weldingdata_id);
      const d = detailById.get(id) || {};
      const size = d.pipesize_name ?? d.pipesize ?? null;
      const svc = d.serviceclass_name ?? d.serviceclass ?? null;
      const spoolRaw = d.spool_id;
      const spool = (spoolRaw === null || spoolRaw === undefined || spoolRaw === '') ? null : Number(spoolRaw);
      const cat = d.weld_category ?? d.weld_category_name ?? null;
      const code = d.code_no ?? null;
      const wtype = d.weldtype_name ?? d.weldtype ?? null;
      pipe_sizes.push(size == null ? null : String(size));
      service_class_names.push(svc == null ? null : String(svc));
      spool_ids.push(Number.isFinite(spool as number) ? (spool as number) : null);
      weld_categories.push(cat == null ? null : String(cat));
      code_nos.push(code == null ? null : String(code));
      weldtype_names.push(wtype == null ? null : String(wtype));
      fluids.push(d.fluid == null ? null : String(d.fluid));
    });

    // Optionally finalize file uploads before update to capture upload_path
    let nextUploadPath: string | null | undefined = uploadPath || null;
    try {
      if (fileUploadRef.current && fileUploadRef.current.hasFilesToUpload()) {
        const docRef = (formIdLabel && String(formIdLabel)) || `RFI-${id}`;
        const up = await fileUploadRef.current.finalizeUpload(docRef);
        if (!up.success) {
          message.error(up.error || 'File upload failed. Changes not saved.');
          return;
        }
        nextUploadPath = up.folderPath || null;
      }
    } catch (e) {
      message.error('File upload failed. Changes not saved.');
      return;
    }

    const payload: Partial<QaqcPipeRfiForms> = {
      issued_by: values.issued_by,
      request_date: values.request_date?.format?.('YYYY-MM-DD') || values.request_date,
      rfi_subject: values.rfi_subject,
      rfi_notes: values.rfi_notes,
      rfi_status: values.rfi_status,
      recipients: values.recipients ?? [],
      recipient_ids,
      cancellation_reason: values.rfi_status === 'cancelled' ? (values.cancellation_reason || null) : null,
      appointees: values.appointees ?? [],
      invitees: values.invitees ?? [],
      appointee_ids: values.appointee_ids ?? [],
      invitee_ids: values.invitee_ids ?? [],
      work_permit_ref_num: values.work_permit_ref_num || null,
      work_permit_description: values.work_permit_description || null,
      inspection_type: values.inspection_type,
      weldingdata_ids,
      weld_no_revs,
      pipe_sizes,
      service_class_names,
      spool_ids,
      weld_categories,
      code_nos,
      weldtype_names,
      fluids,
      upload_path: nextUploadPath ?? null,
    };

    try {
      console.log('[RFI Shared Edit] Final payload before updateData', payload);
      const updRes = await updateData('qaqc_pipe_rfi_forms', id, payload);
      console.log('[RFI Shared Edit] updateData response', updRes);
      try {
        const subject = values.rfi_subject || '';
        const descBase = `${subject} - ${values.issued_by || ''} - ${values.request_date?.format?.('YYYY-MM-DD') || values.request_date || ''}`.trim();
        const base = formIdLabel ? `Pipe RFI ${formIdLabel}: ` : 'Pipe RFI: ';
        const viewPath = `/qaqc/ndt/pipe_inspections/rfi/view/${id}`;
        const appointeeIds: string[] = Array.isArray(values.appointee_ids) ? values.appointee_ids : [];
        const inviteeIds: string[] = Array.isArray(values.invitee_ids) ? values.invitee_ids : [];
        if (appointeeIds.length) {
          await taskService.notify({
            entity: { kind: 'qaqc.pipe_rfi', id },
            assignees: appointeeIds,
            navigateTo: viewPath,
            description: `${base}${descBase} (Appointed)`,
            tag: 'appointed',
            clientKey: `pipe_rfi:${id}:appointed`,
            fanoutPerAssignee: false,
          });
        }
        if (inviteeIds.length) {
          await taskService.notify({
            entity: { kind: 'qaqc.pipe_rfi', id },
            assignees: inviteeIds,
            navigateTo: viewPath,
            description: `${base}${descBase} (Invited)`,
            tag: 'invited',
            clientKey: `pipe_rfi:${id}:invited`,
            fanoutPerAssignee: false,
          });
        }
      } catch {}
      try { await queryClient.invalidateQueries({ queryKey: ['qaqc_pipe_rfi_forms'] }); } catch {}
      try { window.dispatchEvent(new CustomEvent('qaqc_pipe_rfi_forms:changed')); } catch {}
      if (nextUploadPath !== uploadPath) {
        setUploadPath(nextUploadPath || '');
      }
      message.success('Pipe RFI updated');
      navigate('/qaqc/ndt/pipe_inspections/rfi_list', { state: { refreshList: true } });
    } catch (e) {
      message.error('Failed to update Pipe RFI');
    }
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>
            <a onClick={() => navigate(-1)} style={{ marginRight: 16, fontSize: 16 }}>&larr; Back</a>
            {isView ? 'View' : 'Edit'} Pipe RFI Form{formIdLabel ? ` — ${formIdLabel}` : ''}
          </span>
          {!isView && (
            <Button type="default" onClick={() => setShowAppointInvite(v => !v)}>
              {showAppointInvite ? 'Hide Appoint/Invite' : 'Show Appoint/Invite'}
            </Button>
          )}
        </div>
      }
      style={{ maxWidth: 720, margin: '0 auto' }}
    >
      {isView && (
        <style>{`
          /* Improve readability for AntD disabled form controls in view mode */
          .ant-input[disabled],
          .ant-input-disabled,
          .ant-input-affix-wrapper-disabled,
          .ant-picker.ant-picker-disabled,
          .ant-picker.ant-picker-disabled .ant-picker-input > input,
          .ant-select-disabled .ant-select-selector,
          textarea.ant-input[disabled] {
            color: rgba(0, 0, 0, 0.88) !important;
            -webkit-text-fill-color: rgba(0, 0, 0, 0.88) !important;
            background: #fff !important;
            opacity: 1 !important;
          }

          .ant-select-disabled .ant-select-arrow,
          .ant-picker.ant-picker-disabled .ant-picker-suffix {
            color: rgba(0, 0, 0, 0.45) !important;
          }
        `}</style>
      )}
      <Spin spinning={loadingView}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ rfi_status: 'draft', request_date: dayjs(), cancellation_reason: null }}
          size="small"
          disabled={isView}
        >
          {/* Hidden helpers */}
          <Form.Item name="issued_by_id" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="appointees" hidden>
            <Select mode="tags" open={false} style={{ display: 'none' }} />
          </Form.Item>
          <Form.Item name="appointee_ids" hidden>
            <Select mode="tags" open={false} style={{ display: 'none' }} />
          </Form.Item>
          <Form.Item name="invitees" hidden>
            <Select mode="tags" open={false} style={{ display: 'none' }} />
          </Form.Item>
          <Form.Item name="invitee_ids" hidden>
            <Select mode="tags" open={false} style={{ display: 'none' }} />
          </Form.Item>
          <Form.Item name="recipients" hidden>
            <Select mode="tags" open={false} style={{ display: 'none' }} />
          </Form.Item>
          <Form.Item name="recipient_ids" hidden>
            <Select mode="tags" open={false} style={{ display: 'none' }} />
          </Form.Item>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item label={<b>Issued By</b>} required style={{ marginBottom: 8 }}>
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="issued_by" noStyle rules={[{ required: true, message: 'Select issuer' }]}> 
                    <Input readOnly placeholder="Select personnel" />
                  </Form.Item>
                  {!isView && (
                    <Button onMouseDown={(e) => e.preventDefault()} onClick={() => setPersModalOpen(true)}>Select</Button>
                  )}
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<b>Request Date</b>}
                name="request_date"
                rules={[{ required: true, message: 'Please select the request date' }]}
                style={{ marginBottom: 8 }}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={<b>Recipients (send to)</b>} required style={{ marginBottom: 8 }}>
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item name="recipients_display" noStyle rules={[{ required: true, message: 'Select at least one recipient' }]}>
                <TextArea readOnly placeholder="Select one or more personnel" autoSize={{ minRows: 1, maxRows: 6 }} />
              </Form.Item>
              {!isView && (
                <Button onMouseDown={(e) => e.preventDefault()} onClick={() => setRecModalOpen(true)}>Select</Button>
              )}
            </Space.Compact>
          </Form.Item>

          {isView && (
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.appointees !== cur.appointees || prev.invitees !== cur.invitees}>
              {({ getFieldValue }) => {
                const app = getFieldValue('appointees') || [];
                const inv = getFieldValue('invitees') || [];
                return (
                  <>
                    {Array.isArray(app) && app.length > 0 && (
                      <Form.Item label={<b>Appointees</b>} style={{ marginBottom: 8 }}>
                        <TextArea rows={2} readOnly value={app.join('; ')} />
                      </Form.Item>
                    )}
                    {Array.isArray(inv) && inv.length > 0 && (
                      <Form.Item label={<b>Invitees</b>} style={{ marginBottom: 8 }}>
                        <TextArea rows={2} readOnly value={inv.join('; ')} />
                      </Form.Item>
                    )}
                  </>
                );
              }}
            </Form.Item>
          )}

          <Row gutter={12}>
            <Col xs={24} md={8}>
              <Form.Item label={<b>Work Permit Ref No.</b>} style={{ marginBottom: 8 }}>
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="work_permit_ref_num" noStyle>
                    <Input readOnly placeholder="e.g. WP-2025-00123" />
                  </Form.Item>
                  {!isView && (
                    <Button htmlType="button" onClick={() => setWpModalOpen(true)}>Select</Button>
                  )}
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              <Form.Item
                label={<b>Work Permit Description</b>}
                name="work_permit_description"
                style={{ marginBottom: 8 }}
              >
                <Input readOnly placeholder="Auto-filled from Work Permit selection" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Form.Item name="inspection_type" label={<b>Inspection Type</b>} rules={[{ required: true }]} style={{ marginBottom: 8 }}>
                <Radio.Group optionType="button" buttonStyle="solid" style={{ display: 'inline-flex', flexWrap: 'nowrap' }}>
                  {['BC','VC','PT','RT','MT','UT','PMI','PWHT'].map(s => (<Radio.Button key={s} value={s}>{s}</Radio.Button>))}
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <b>Welds</b>
                      <span style={{ opacity: 0.8 }}>({selectedWelds.length})</span>
                    </span>
                    {!isView && (
                      <span style={{ marginLeft: '20px', display: 'inline-flex', gap: 4 }}>
                        <Button onMouseDown={(e) => e.preventDefault()} onClick={() => { setWeldModalMode('add'); setWeldModalOpen(true); }} icon={<PlusOutlined />} style={{ color: '#1677ff' }} />
                        <Button onMouseDown={(e) => e.preventDefault()} onClick={() => {
                          if (!selectedRowKeys.length) { message.info('Select welds to remove'); return; }
                          const removeIds = new Set(selectedRowKeys.map(k => String(k).split('|')[0]));
                          setSelectedWelds(prev => prev.filter(w => !removeIds.has(String(w.weldingdata_id))));
                          setSelectedWeldDetails(prev => prev.filter((w: any) => !removeIds.has(String(w.weldingdata_id))));
                          setSelectedRowKeys([]);
                        }} icon={<MinusOutlined />} style={{ color: '#f5222d' }} />
                      </span>
                    )}
                  </div>
                }
                style={{ marginBottom: 8 }}
              >
                <Space.Compact style={{ width: '100%' }}>
                  <Input readOnly value={`${selectedWelds.length} selected`} />
                </Space.Compact>
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginTop: 4, marginBottom: 12 }}>
            <style>{`
              .rfi-welds-table .ant-table-tbody > tr > td,
              .rfi-welds-table .ant-table-thead > tr > th { padding-top: 4px !important; padding-bottom: 4px !important; }
              .rfi-welds-table .ant-table-cell { white-space: nowrap; }
            `}</style>
            <Table
              className="rfi-welds-table"
              key={`welds-${selectedWelds.map((w: any) => `${String(w.weldingdata_id)}|${String(w.weld_no_rev ?? '')}`).join(',')}`}
              size="small"
              tableLayout="fixed"
              pagination={false}
              columns={[
                { title: 'Welding ID', dataIndex: 'weldingdata_id', key: 'weldingdata_id', width: 210, ellipsis: true },
                { title: 'W.rev', dataIndex: 'weld_no_rev', key: 'weld_no_rev', width: 50, ellipsis: true },
                { title: 'P.Size', key: 'pipesize', width: 60, ellipsis: true, render: (_: any, r: any) => r.pipesize_name || r.pipesize || '' },
                { title: 'Class', key: 'serviceclass', width: 60, ellipsis: true, render: (_: any, r: any) => r.serviceclass_name || r.serviceclass || '' },
                { title: 'Fluid', dataIndex: 'fluid', key: 'fluid', width: 60, ellipsis: true },
                { title: 'Spool', dataIndex: 'spool_id', key: 'spool_id', width: 70, ellipsis: true },
                { title: 'F/S', key: 'weld_category', width: 40, ellipsis: true, render: (_: any, r: any) => r.weld_category || r.weld_category_name || '' },
                { title: 'Type', key: 'weldtype', width: 50, ellipsis: true, render: (_: any, r: any) => r.weldtype_name || r.weldtype || '' },
                { title: 'Code', dataIndex: 'code_no', key: 'code_no', width: 50, ellipsis: true },
              ] as any}
              dataSource={displayWeldRows}
              rowKey={(r: any) => r.key}
              rowSelection={isView ? undefined : {
                type: 'checkbox',
                selectedRowKeys,
                onChange: (keys) => setSelectedRowKeys(keys as React.Key[]),
                columnWidth: 24,
              }}
              style={{ width: '100%' }}
            />
          </div>

          <Form.Item label={<b>Subject</b>} name="rfi_subject" rules={isView ? undefined : [{ required: true, message: 'Please enter the subject' }]} style={{ marginBottom: 8 }}>
            <Input />
          </Form.Item>

          <Form.Item label={<b>Notes</b>} name="rfi_notes" style={{ marginBottom: 8 }}>
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ minWidth: 90, fontWeight: 600 }}>Status</span>
              <Form.Item name="rfi_status" noStyle rules={isView ? undefined : [{ required: true, message: 'Please select the status' }]}> 
                <Select style={{ minWidth: 160 }} disabled={isView}>
                  <Option value="draft">Draft</Option>
                  <Option value="active">Active</Option>
                  <Option value="archived">Archived</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
              {!isView && (
                <Button type="primary" style={{ backgroundColor: '#faad14', borderColor: '#faad14', marginLeft: 'auto' }} onClick={() => setShowAppointInvite(v => !v)}>
                  {showAppointInvite ? 'Hide Appoint/Invite' : 'Show Appoint/Invite'}
                </Button>
              )}
            </div>
          </Form.Item>

          {!isView && (
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.rfi_status !== cur.rfi_status}>
              {({ getFieldValue }) => {
                const status = getFieldValue('rfi_status');
                return status === 'cancelled' ? (
                  <Form.Item label={<b>Cancellation Reason</b>} name="cancellation_reason" rules={[{ required: true, message: 'Please provide a cancellation reason' }]} style={{ marginBottom: 8 }}>
                    <TextArea rows={3} />
                  </Form.Item>
                ) : null;
              }}
            </Form.Item>
          )}

          {!isView && showAppointInvite && (
            <Form.Item label={<b>Appointees</b>} style={{ marginBottom: 8 }}>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="appointees_display" noStyle>
                  <TextArea readOnly placeholder="Select appointees" autoSize={{ minRows: 1, maxRows: 6 }} />
                </Form.Item>
                <Button onMouseDown={(e) => e.preventDefault()} onClick={() => setAppModalOpen(true)}>Select</Button>
              </Space.Compact>
            </Form.Item>
          )}

          {!isView && showAppointInvite && (
            <Form.Item label={<b>Invitees</b>} style={{ marginBottom: 8 }}>
              <Space.Compact style={{ width: '100%' }}>
                <Form.Item name="invitees_display" noStyle>
                  <TextArea readOnly placeholder="Select invitees" autoSize={{ minRows: 1, maxRows: 6 }} />
                </Form.Item>
                <Button onMouseDown={(e) => e.preventDefault()} onClick={() => setInvModalOpen(true)}>Select</Button>
              </Space.Compact>
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {!isView && (<FileUploadTool ref={fileUploadRef} buttonText="Upload Files" />)}
              {!isView && uploadPath && uploadPath.trim() !== '' && (
                <FileDownloadTool
                  buttonText={(
                    <Button
                      style={{
                        backgroundColor: ((): string => {
                          const s = String(form.getFieldValue('rfi_status') || currentStatus || '').toLowerCase();
                          if (s === 'active') return '#52c41a';
                          if (s === 'archived') return '#bfbfbf';
                          if (s === 'cancelled') return '#ff4d4f';
                          return '#faad14';
                        })(),
                        color: '#fff',
                        borderColor: 'transparent',
                      }}
                    >
                      Attached Files
                    </Button>
                  )}
                  contentPath={uploadPath}
                />
              )}
              {!isView && (<Button action="edit" type="primary" htmlType="submit">Save Changes</Button>)}
            </div>
          </Form.Item>
        </Form>

        {/* View-mode attachments (outside disabled Form) */}
        {isView && uploadPath && uploadPath.trim() !== '' && (
          <div style={{ marginTop: 12 }}>
            <FileDownloadTool
              buttonText={(
                <Button
                  style={{
                    backgroundColor: ((): string => {
                      const s = String(form.getFieldValue('rfi_status') || currentStatus || '').toLowerCase();
                      if (s === 'active') return '#52c41a';
                      if (s === 'archived') return '#bfbfbf';
                      if (s === 'cancelled') return '#ff4d4f';
                      return '#faad14';
                    })(),
                    color: '#fff',
                    borderColor: 'transparent',
                  }}
                >
                  Attached Files
                </Button>
              )}
              contentPath={uploadPath}
            />
          </div>
        )}

        {/* Modals (openable only in edit) */}
        {!isView && (
          <PersonnelSelect
            open={recModalOpen}
            onCancel={() => setRecModalOpen(false)}
            onOk={(val: any) => {
              const arr = Array.isArray(val) ? val : [val];
              const names: string[] = [];
              const codes: string[] = [];
              arr.forEach((s: any) => {
                const str = String(s);
                const idx = str.indexOf(' - ');
                if (idx >= 0) {
                  codes.push(str.slice(0, idx).trim());
                  names.push(str.slice(idx + 3).trim());
                } else {
                  names.push(str.trim());
                }
              });
              const issuerPid: string | undefined = form.getFieldValue('issued_by_id');
              if (issuerPid && !codes.includes(String(issuerPid))) codes.push(String(issuerPid));
              form.setFieldsValue({ recipients: names, recipient_ids: codes, recipients_display: names.join('; ') });
              setRecModalOpen(false);
            }}
            multi
          />
        )}

        {!isView && (
          <PersonnelSelect
            open={appModalOpen}
            onCancel={() => setAppModalOpen(false)}
            onOk={(val: any) => {
              const arr = Array.isArray(val) ? val : [val];
              const names: string[] = [];
              const codes: string[] = [];
              arr.forEach((s: any) => {
                const str = String(s);
                let pid: string | undefined;
                let nameStr: string = str;
                const idx = str.indexOf(' - ');
                if (idx >= 0) {
                  pid = str.slice(0, idx).trim();
                  nameStr = str.slice(idx + 3).trim();
                }
                if (!pid) {
                  try {
                    const list = (personnelData?.data || []) as any[];
                    const rec = list.find((p: any) => `${p.first_name || ''} ${p.last_name || ''}`.trim() === nameStr);
                    if (rec) pid = String(rec.pers_id || rec.id || '');
                  } catch {}
                }
                const companyCode = pid ? String(pid).slice(0, 3) : '';
                const display = companyCode ? `${nameStr} (${companyCode})` : nameStr;
                if (pid) codes.push(pid);
                names.push(display);
              });
              const display = names.join('; ');
              const setVals: any = { appointees: names, appointee_ids: codes, appointees_display: display };
              if (codes.length > 0) setVals.rfi_status = 'active';
              form.setFieldsValue(setVals);
              setAppModalOpen(false);
            }}
            multi
          />
        )}

        {!isView && (
          <PersonnelSelect
            open={invModalOpen}
            onCancel={() => setInvModalOpen(false)}
            onOk={(val: any) => {
              const arr = Array.isArray(val) ? val : [val];
              const names: string[] = [];
              const codes: string[] = [];
              arr.forEach((s: any) => {
                const str = String(s);
                let pid: string | undefined;
                let nameStr: string = str;
                const idx = str.indexOf(' - ');
                if (idx >= 0) {
                  pid = str.slice(0, idx).trim();
                  nameStr = str.slice(idx + 3).trim();
                }
                if (!pid) {
                  try {
                    const list = (personnelData?.data || []) as any[];
                    const rec = list.find((p: any) => `${p.first_name || ''} ${p.last_name || ''}`.trim() === nameStr);
                    if (rec) pid = String(rec.pers_id || rec.id || '');
                  } catch {}
                }
                const companyCode = pid ? String(pid).slice(0, 3) : '';
                const display = companyCode ? `${nameStr} (${companyCode})` : nameStr;
                if (pid) codes.push(pid);
                names.push(display);
              });
              const display = names.join('; ');
              const setVals: any = { invitees: names, invitee_ids: codes, invitees_display: display };
              if (codes.length > 0) setVals.rfi_status = 'active';
              form.setFieldsValue(setVals);
              setInvModalOpen(false);
            }}
            multi
          />
        )}

        {!isView && (
          <WPermitSelect
            open={wpModalOpen}
            value={form.getFieldValue('work_permit_ref_num')}
            onCancel={() => setWpModalOpen(false)}
            onOk={([refNum, desc]) => {
              form.setFieldsValue({ work_permit_ref_num: refNum, work_permit_description: desc });
              setWpModalOpen(false);
            }}
          />
        )}

        {!isView && (
          <WeldSelectModal
            open={weldModalOpen}
            mode={weldModalMode}
            value={selectedWelds}
            returnAllModes
            onCancel={() => setWeldModalOpen(false)}
            onOk={(vals) => {
              const payload = (vals && (vals as any).modes) ? (vals as any) : { modes: { basic: vals as WeldSelectValue[], full: undefined } };
              const basic: WeldSelectValue[] = payload.modes.basic || [];
              const full: any[] | undefined = payload.modes.full;
              const toNumRev = (v: any): number => {
                if (v === null || v === undefined || v === '') return -Infinity;
                const n = Number(v);
                return Number.isFinite(n) ? n : -Infinity;
              };
              if (weldModalMode === 'add') {
                const mergedBasicSource = [...selectedWelds, ...basic];
                const byIdBasic = new Map<string, WeldSelectValue>();
                let conflicts = 0;
                mergedBasicSource.forEach(w => {
                  const id = String(w.weldingdata_id);
                  const existing = byIdBasic.get(id);
                  if (!existing) {
                    byIdBasic.set(id, w);
                  } else {
                    conflicts += 1;
                    const curRev = toNumRev(existing.weld_no_rev);
                    const newRev = toNumRev(w.weld_no_rev);
                    if (newRev > curRev) byIdBasic.set(id, w);
                  }
                });
                const newBasic = Array.from(byIdBasic.values());
                if (conflicts > 0) message.info('Multiple revisions selected for some welds. Kept highest revision.');
                setSelectedWelds(newBasic);

                if (full && Array.isArray(full)) {
                  const prevById = new Map<string, any>();
                  selectedWeldDetails.forEach((p: any) => prevById.set(String(p.weldingdata_id), p));
                  const fullByKey = new Map<string, any>();
                  full.forEach((f: any) => fullByKey.set(`${String(f.weldingdata_id)}|${String(f.weld_no_rev)}`, f));
                  const byIdFull = new Map<string, any>();
                  newBasic.forEach(w => {
                    const id = String(w.weldingdata_id);
                    const existing = byIdFull.get(id) || prevById.get(id);
                    if (!existing) {
                      const f = fullByKey.get(`${id}|${String(w.weld_no_rev)}`) || prevById.get(id);
                      if (f) byIdFull.set(id, f);
                      else byIdFull.set(id, { weldingdata_id: id, weld_no_rev: w.weld_no_rev });
                    } else {
                      const curRev = toNumRev(existing.weld_no_rev);
                      const newRev = toNumRev(w.weld_no_rev);
                      if (newRev > curRev) {
                        const f = fullByKey.get(`${id}|${String(w.weld_no_rev)}`) || prevById.get(id) || { weldingdata_id: id, weld_no_rev: w.weld_no_rev };
                        byIdFull.set(id, f);
                      } else {
                        byIdFull.set(id, existing);
                      }
                    }
                  });
                  setSelectedWeldDetails(Array.from(byIdFull.values()));
                } else {
                  const prevById = new Map<string, any>();
                  selectedWeldDetails.forEach((p: any) => prevById.set(String(p.weldingdata_id), p));
                  const next: any[] = [];
                  newBasic.forEach(w => {
                    const id = String(w.weldingdata_id);
                    const prev = prevById.get(id);
                    if (prev) next.push(prev);
                    else next.push({ weldingdata_id: id, weld_no_rev: w.weld_no_rev });
                  });
                  setSelectedWeldDetails(next);
                }
              } else {
                const removeList = basic || [];
                if (!removeList.length) {
                  message.info('Select welds to remove');
                } else {
                  const removeIds = new Set(removeList.map(v => String(v.weldingdata_id)));
                  setSelectedWelds(prev => prev.filter(w => !removeIds.has(String(w.weldingdata_id))));
                  setSelectedWeldDetails(prev => prev.filter((w: any) => !removeIds.has(String(w.weldingdata_id))));
                }
              }
              setWeldModalOpen(false);
            }}
          />
        )}
      </Spin>
    </Card>
  );
};

export default QaqcPipeRfiFormShared;
